import type { QueryKey } from '@tanstack/react-query';

import { AsyncComboBoxBase } from './AsyncComboBoxBase';
import type { MultiComboBoxProps } from './MultiComboBox';
import { ComboBoxMode } from './types/ComboBoxMode';
import type { ComboBoxOption } from './types/ComboBoxOption';

interface AsyncComboBoxOptionsProps<Option extends ComboBoxOption> {
    optionsQueryKey: (search: string) => QueryKey;
    optionsQueryFn: (search: string) => Promise<Option[]>;
    customOptionsFilter?: (options: Option[], search: string) => Option[];
    prefetchOnHover?: boolean;
}

export type AsyncMultiComboBoxProps<Option extends ComboBoxOption = ComboBoxOption> = Omit<
    MultiComboBoxProps<Option>,
    'options'
> &
    AsyncComboBoxOptionsProps<Option> & {
        selectedOptionsQueryKey: (values: Array<Option['value']>) => QueryKey;
        selectedOptionsQueryFn: (values: Array<Option['value']>) => Promise<Option[]>;
    };

export function AsyncMultiComboBox<Option extends ComboBoxOption = ComboBoxOption>({
    value = [],
    optionsQueryKey,
    optionsQueryFn,
    customOptionsFilter,
    selectedOptionsQueryKey,
    selectedOptionsQueryFn,
    prefetchOnHover,
    onChange,
    isClearable = true,
    clearSearchOnSelect = true,
    ...props
}: AsyncMultiComboBoxProps<Option>) {
    return (
        <AsyncComboBoxBase<Option>
            {...props}
            mode={ComboBoxMode.Multi}
            value={value}
            optionsQueryKey={optionsQueryKey}
            optionsQueryFn={optionsQueryFn}
            selectedOptionsQueryKey={selectedOptionsQueryKey}
            selectedOptionsQueryFn={selectedOptionsQueryFn}
            customOptionsFilter={customOptionsFilter}
            isClearable={isClearable}
            clearSearchOnSelect={clearSearchOnSelect}
            prefetchOnHover={prefetchOnHover}
            onSelectionChange={(nextSelectedOptions, meta) => {
                onChange?.(
                    nextSelectedOptions.map((option) => option.value),
                    meta
                );
            }}
        />
    );
}
